const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const motion = () => reducedMotion.matches ? 'auto' : 'smooth';
const cleanup = new WeakMap();
const campaignControllers = new WeakMap();
const initialize = (root) => {
  root.querySelectorAll('[data-rebranding-carousel]:not([data-rebranding-ready])').forEach((carousel) => {
    const track = carousel.querySelector('[data-rebranding-track]');
    const cards = [...track.children];
    if (!cards.length) return;
    carousel.dataset.rebrandingReady = 'true';
    const navigation = carousel.querySelector('[data-rebranding-navigation]');
    const dotsContainer = carousel.querySelector('[data-rebranding-dots]');
    const controller = new AbortController();
    const { signal } = controller;
    let positions = [];
    let selected = 0;
    let frame;
    const offset = () => Math.abs(track.scrollLeft);
    const update = () => {
      selected = positions.reduce((best, position, i) => Math.abs(position - offset()) < Math.abs(positions[best] - offset()) ? i : best, 0);
      dotsContainer.querySelectorAll('button').forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === selected)));
    };
    const select = (index) => {
      if (positions.length < 2) return;
      selected = (index + positions.length) % positions.length;
      track.scrollTo({ left: positions[selected] * (getComputedStyle(track).direction === 'rtl' ? -1 : 1), behavior: motion() });
    };
    const layout = () => {
      const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
      const first = cards[0].getBoundingClientRect().left;
      const next = cards.map(card => Math.min(maximum, Math.abs(card.getBoundingClientRect().left - first)));
      positions = next.filter((position, i) => i === 0 || Math.abs(position - next[i - 1]) > 1);
      navigation.hidden = maximum <= 1 || positions.length < 2;
      if (dotsContainer.children.length !== positions.length) {
        dotsContainer.replaceChildren(...positions.map((_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.dataset.rebrandingDot = String(index);
          dot.setAttribute('aria-label', dotsContainer.dataset.label.replace('[index]', index + 1));
          return dot;
        }));
      }
      update();
    };
    dotsContainer.addEventListener('click', (event) => {
      const dot = event.target.closest('[data-rebranding-dot]');
      if (dot) select(Number(dot.dataset.rebrandingDot));
    }, { signal });
    carousel.querySelectorAll('[data-rebranding-step]').forEach(button => button.addEventListener('click', () => select(selected + Number(button.dataset.rebrandingStep)), { signal }));
    track.addEventListener('scroll', () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update); }, { passive: true, signal });
    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        const direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
        select(selected + (event.key === 'ArrowRight' ? direction : -direction));
      }
    }, { signal });
    const observer = new ResizeObserver(layout);
    observer.observe(track);
    layout();
    cleanup.set(carousel, () => { observer.disconnect(); controller.abort(); cancelAnimationFrame(frame); });
  });
  root.querySelectorAll('[data-rebranding-campaign]:not([data-rebranding-ready])').forEach((campaign) => {
    const slides = [...campaign.querySelectorAll('[data-rebranding-slide]')];
    campaign.dataset.rebrandingReady = 'true';
    if (slides.length < 2) return;
    const controller = new AbortController();
    const { signal } = controller;
    const pause = campaign.querySelector('[data-rebranding-pause]');
    let index = 0;
    let timer;
    let paused = campaign.dataset.autoplay !== 'true' || reducedMotion.matches;
    let hovering = false;
    let visible = false;
    const show = (next) => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        slide.setAttribute('aria-hidden', String(i !== index));
        if (i === index || i === (index + 1) % slides.length) slide.querySelectorAll('img').forEach(img => img.loading = 'eager');
      });
    };
    slides[1].querySelectorAll('img').forEach(img => img.loading = 'eager');
    const sync = () => {
      clearInterval(timer);
      pause.setAttribute('aria-pressed', String(paused));
      pause.setAttribute('aria-label', paused ? pause.dataset.playLabel : pause.dataset.pauseLabel);
      pause.querySelector('[data-rebranding-pause-icon]').textContent = paused ? '▷' : 'Ⅱ';
      if (!paused && !hovering && visible && !document.hidden && !campaign.contains(document.activeElement)) timer = setInterval(() => show(index + 1), Math.max(1000, Number(campaign.dataset.interval) || 1000));
    };
    pause.addEventListener('click', () => { paused = !paused; sync(); }, { signal });
    campaign.addEventListener('mouseenter', () => { hovering = true; sync(); }, { signal });
    campaign.addEventListener('mouseleave', () => { hovering = false; sync(); }, { signal });
    campaign.addEventListener('focusin', sync, { signal });
    campaign.addEventListener('focusout', () => requestAnimationFrame(sync), { signal });
    document.addEventListener('visibilitychange', sync, { signal });
    const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync(); });
    observer.observe(campaign);
    campaignControllers.set(campaign, { show: (slide) => { paused = true; show(slide); sync(); } });
    cleanup.set(campaign, () => { clearInterval(timer); observer.disconnect(); controller.abort(); });
    sync();
  });
  root.querySelectorAll('.rebranding-inline-video:not([data-rebranding-ready])').forEach((video) => {
    video.dataset.rebrandingReady = 'true';
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !document.hidden) video.play().catch(() => {});
      else video.pause();
    }, { threshold: .1 });
    observer.observe(video);
    cleanup.set(video, () => { observer.disconnect(); video.pause(); });
  });
};
initialize(document);
document.addEventListener('shopify:section:load', (event) => initialize(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-rebranding-ready]').forEach(element => cleanup.get(element)?.());
});
document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target.closest('[data-rebranding-slide]');
  if (slide) {
    const campaign = slide.closest('[data-rebranding-campaign]');
    campaignControllers.get(campaign)?.show([...campaign.querySelectorAll('[data-rebranding-slide]')].indexOf(slide));
  }
});
document.addEventListener('visibilitychange', () => {
  document.querySelectorAll('.rebranding-inline-video').forEach(video => {
    if (document.hidden) video.pause();
    else { const rect = video.getBoundingClientRect(); if (rect.bottom > 0 && rect.top < innerHeight) video.play().catch(() => {}); }
  });
});
document.addEventListener('click', (event) => {
  if (event.target.closest('[data-rebranding-top]')) window.scrollTo({ top: 0, behavior: motion() });
  if (event.target.closest('[data-rebranding-locale]')) document.getElementById('country-modal')?.showModal();
  const opener = event.target.closest('[data-rebranding-open]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.rebrandingOpen);
    if (dialog) {
      opener.querySelector('video')?.pause();
      dialog.showModal();
      const video = dialog.querySelector('video');
      if (video) { video.muted = true; video.loop = true; video.play().catch(() => {}); }
    }
  }
  const closer = event.target.closest('[data-rebranding-close]');
  if (closer) closer.closest('dialog').close();
});
document.addEventListener('close', (event) => {
  if (event.target.matches?.('.rebranding-video-dialog')) {
    event.target.querySelector('video')?.pause();
    event.target.closest('.rebranding-video-card')?.querySelector('.rebranding-inline-video')?.play().catch(() => {});
  }
}, true);
